import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../store";
import {
  clearPendingAttempt,
  clearPendingVocabWords,
} from "../../../slices/deferredActionsSlice";
import { useCreateAttemptMutation } from "../../../api/attemptApi";
import { useAddCustomWordMutation } from "../../../api/vocabApi";
import { addToast } from "../../../slices/toastSlice";

/**
 * Executes deferred actions (attempt submissions, vocab saves) after authentication.
 * Rendered in AppContainer when isAuthenticated is true.
 */
const DeferredActionsExecutor = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pendingAttempt, pendingVocabWords } = useSelector(
    (s: RootState) => s.deferredActions
  );
  const [createAttempt] = useCreateAttemptMutation();
  const [addCustomWord] = useAddCustomWordMutation();
  const executedRef = useRef(false);

  useEffect(() => {
    if (executedRef.current) return;

    const execute = async () => {
      let executedAny = false;

      // Execute pending attempt
      if (pendingAttempt) {
        try {
          await createAttempt({
            exerciseId: pendingAttempt.exerciseId,
            attemptData: pendingAttempt.attemptData,
          });
          dispatch(addToast({
            message: t("deferredActions.attemptSaved", "Your results have been saved!"),
            type: "success",
          }));
          executedAny = true;
        } catch { /* silently fail */ }
        dispatch(clearPendingAttempt());
      }

      // Execute pending vocab words
      if (pendingVocabWords.length > 0) {
        let savedCount = 0;
        for (const wordData of pendingVocabWords) {
          try {
            await addCustomWord(wordData).unwrap();
            savedCount++;
          } catch { /* continue with next word */ }
        }
        if (savedCount > 0) {
          dispatch(addToast({
            message: t("deferredActions.vocabSaved", "{{count}} word(s) added to your vocabulary!", { count: savedCount }),
            type: "success",
          }));
          executedAny = true;
        }
        dispatch(clearPendingVocabWords());
      }

      if (executedAny) {
        executedRef.current = true;
      }
    };

    execute();
  }, [pendingAttempt, pendingVocabWords, createAttempt, addCustomWord, dispatch, t]);

  return null;
};

export default DeferredActionsExecutor;
